/*
Author: Chathuraka Mallawa Arachchi
Date: 25/09/2024
Copyright © 2024 Infix!. All rights reserved.
*/

import React, { useEffect, useRef, useState } from 'react'
import Select, { components, MultiValue } from 'react-select'
import {
    DropDownOption,
    nodeConfigurations,
} from '../survey/SurveyModel/types/nodeTypes'
import Errors from './Error'

// Hide input, show nothing
const DummyControl = ({}: any) => <div style={{ display: 'none' }}></div>

const MultiSelectNoInput = (props: any) => {
    const {
        options,
        name,
        form,
        handleChange,
        value,
        excludeOptions,
        propDivClasses = '',
        isInValid = false,
        onBlur,
        disabled,
        configurations,
        menuPositionOffset,
        onClose,
        isOpen,
    } = props
    const [optionList, setOptionList] = useState<DropDownOption[]>(options)
    //const [textValue, setTextValue] = React.useState<string> ();
    const [isFocused, setIsFocused] = useState(false)
    const [isActive, setIsActive] = useState(false)
    const [menuOpen, setMenuOpen] = useState(true)
    const [isVisible, setIsVisible] = useState(true)
    const wrapperRef = useRef<HTMLDivElement>(null);

    const prepareTypeOptions = () => {
        let newoptionList: DropDownOption[] = []
        if (excludeOptions.length) {
            newoptionList = options.filter((rowItem: DropDownOption) => {
                return !excludeOptions.some(
                    (item: DropDownOption) =>
                        item.value === rowItem.value && item.value !== value
                )
            })
        }

        setOptionList(newoptionList)
    }

    useEffect(() => {
        if (options?.length) {
            setOptionList(options)
        }
    }, [options, excludeOptions])

    useEffect(() => {
        if (excludeOptions && excludeOptions?.length && options.length) {
            prepareTypeOptions()
        }
    }, [excludeOptions, options])

    const handleOnBlure = () => {
        onBlur()
        setIsFocused(false)
    }

    const formatOptionLabel = (
        { label, description }: DropDownOption,
        { context }: any
    ) => {
        return context === 'menu' ? (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
                <div>{label}</div>
                <div>{description}</div>
            </div>
        ) : (
            <div>{label}</div>
        )
    }

    // Custom Option with Checkbox
    // const CheckboxOption = (props: any) => {
    //     return (
    //         <components.Option {...props}>
    //             <input
    //                 type="checkbox"
    //                 checked={props.isSelected}
    //                 onChange={() => null}
    //                 style={{
    //                     marginRight: 10,
    //                     width: 18,
    //                     height: 18,
    //                     border: '2px solid gray',
    //                     borderRadius:0,
    //                     backgroundColor: 'white',
    //                 }}
    //             />
    //             <label>{props.label}</label>
    //         </components.Option>
    //     )
    // }

    const CheckboxOption = (props: any) => {
        return (
            <components.Option {...props}>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    <input
                        type="checkbox"
                        checked={value?.includes(props.label)}
                        onClick={() => {}}
                        style={{
                            width: 0,
                            height: 0,
                        }}
                    />
                    <svg width="18" height="18" viewBox="0 0 18 18">
                        <rect
                            x="0"
                            y="0"
                            width="18"
                            height="18"
                            rx="0"
                            ry="0"
                            fill={props.isSelected ? 'white' : 'white'}
                            stroke="black"
                            strokeWidth="1"
                        />
                        {props.isSelected && (
                            <polyline
                                points="4.5,9 7.5,12.5 13.5,5.5"
                                fill="none"
                                stroke="#3F3F3F"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        )}
                    </svg>
                    <label
                        style={{
                            marginLeft: '8px',
                            fontSize: '14px',
                            fontWeight: '400',
                        }}
                    >
                        {props.label}
                    </label>
                </div>
            </components.Option>
        )
    }

    const handleCheckboxChange = (
        selectedOptions: MultiValue<DropDownOption>
    ) => {
        const allOption = options.find(
            (opt: DropDownOption) => opt.value === '0'
        )
        const regularOptions = options.filter(
            (opt: DropDownOption) => opt.value !== '0'
        )

        const selected = Array.isArray(selectedOptions) ? selectedOptions : []
        if (
            selected.some((opt: DropDownOption) => opt.value === '0') &&
            !value?.some((opt: DropDownOption) => opt.value === '0')
        ) {
            handleChange(options)
            return
        }
        if (
            !selected.some((opt: DropDownOption) => opt.value === '0') &&
            value?.some((opt: DropDownOption) => opt.value === '0')
        ) {
            handleChange([])
            return
        }
        if (!selected.some((opt: DropDownOption) => opt.value === '0')) {
            if (
                regularOptions.every((opt: DropDownOption) =>
                    selected.some((s: DropDownOption) => s.value === opt.value)
                )
            ) {
                if (allOption) {
                    handleChange([...selected, allOption])
                } else {
                    handleChange(selected)
                }
                return
            }

            handleChange(selected)
            return
        }
        if (
            selected.some((opt: DropDownOption) => opt.value === '0') &&
            value?.some((opt: DropDownOption) => opt.value === '0')
        ) {
            const newSelection = selected.filter(
                (opt: DropDownOption) => opt.value !== '0'
            )
            handleChange(newSelection)
            return
        }
        handleChange(selected)
    }

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                onClose(); // Notify parent to close
                setMenuOpen(false);
                setIsVisible(true)
            }
        }

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            setMenuOpen(true);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [isOpen, onClose]);


    return (
        <React.Fragment>
            {isVisible ? (
                <div className={`${isInValid ? 'error' : ''}`}>
                    <div
                        ref={wrapperRef}
                        className={`${propDivClasses} input-div ${configurations?.some((config: nodeConfigurations) => config.key === 'FieldWidthPct' && config.value === '0.5') ? 'width-half' : ''} ${isFocused ? 'input-div-focus' : ''} ${isActive ? 'input-div-active' : ''}`}
                    >
                        <Select
                            className={`
            custom-select
            ${configurations?.some((config: nodeConfigurations) => config.key === 'FieldWidthPct' && config.value === '0.5') ? 'custom-select-lg' : ''}
            ${disabled ? 'disabled' : ''}`}
                            name={name}
                            classNamePrefix="custom-select"
                            placeholder={`${disabled ? '' : 'Select'}`}
                            options={optionList}
                            onBlur={handleOnBlure}
                            onChange={handleCheckboxChange}
                            value={value || ''}
                            menuIsOpen={menuOpen}
                            closeMenuOnSelect={false}
                            onMenuOpen={() => setIsActive(true)}
                            menuPosition="absolute"
                            //onMenuClose={() => setIsActive(false)}
                            isDisabled={disabled}
                            onMenuClose={() => setMenuOpen(false)}
                            hideSelectedOptions={false}
                            isMulti
                            onFocus={() => {
                                setIsFocused(true)
                            }}
                            isSearchable={false}
                            components={{
                                Option: CheckboxOption,
                                Control: DummyControl,
                                IndicatorsContainer: () => null, // Hide clear and dropdown arrow
                                MultiValue: () => null, // Hide selected tags
                                MultiValueRemove: () => null,
                            }}
                            formatOptionLabel={formatOptionLabel}
                            styles={{
                                control: (baseStyles, state) => ({
                                    ...baseStyles,
                                    borderColor: state.isFocused
                                        ? 'var(--comp-field-bordercolor-default)'
                                        : 'var(--comp-field-bordercolor-default)',

                                    borderRadius: '0',
                                    boxShadow: 'none', // Remove the blue border (box-shadow)
                                    ':hover': {
                                        borderColor:
                                            'var(--comp-field-bordercolor-default)', // Change border color on hover
                                    },
                                    fontSize:
                                        'var(--comp-field-fontsize-input-default)',
                                    fontWeight:
                                        'var(--comp-field-fontweight-input-default)',
                                    color: 'var(--comp-field-fontcolour-default)',
                                    fontFamily: '$Lexend',
                                    height: 'var(--input-height)',
                                    minHeight: 'var(--input-height)',
                                }),
                                dropdownIndicator: (
                                    provided: any,
                                    state: any
                                ) => ({
                                    ...provided,
                                    color: state.isFocused
                                        ? 'var(--comp-field-fontcolour-default)'
                                        : 'var(--comp-icon-color-positive)', // Change arrow color
                                    ':hover': {
                                        color: 'darkblue', // Change color on hover
                                    },
                                    display: disabled ? 'none' : 'flex',
                                }),
                                menu: (provided: any) => {
                                    const hasOffset =
                                        menuPositionOffset?.top ||
                                        menuPositionOffset?.left

                                    const hasTransform =
                                        menuPositionOffset?.transform

                                    const menuMinWidth =
                                        menuPositionOffset?.minWidth ??
                                        `calc(100% + 4px)`

                                    return {
                                        ...provided,
                                        borderRadius: 0,
                                        borderBlockColor:
                                            'var(--comp-field-bordercolor-default)',
                                        margin: 0,
                                        padding: '0px 0px',
                                        minWidth: menuMinWidth,
                                        ...(hasOffset && {
                                            position: 'relative',
                                            top:
                                                menuPositionOffset?.top ??
                                                '0px',
                                            left:
                                                menuPositionOffset?.left ??
                                                '0px',
                                        }),
                                        transform: hasTransform
                                            ? menuPositionOffset?.transform
                                            : 'none',
                                    }
                                },
                                menuList: (provided) => ({
                                    ...provided,
                                    maxHeight: 'none',
                                    height: 'auto',
                                    overflow: 'visible',
                                    padding:
                                        'var(--comp-listScreenFilterMenu-padding-xs, 8px) 0',
                                }),
                                valueContainer: (baseStyles) => ({
                                    ...baseStyles,
                                    //padding: 'var(--comp-field-padding-fieldpadding)',
                                    height: '36px',
                                    display: 'flex',
                                    flexWrap: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                }),
                                singleValue: (baseStyles) => ({
                                    ...baseStyles,
                                    fontSize:
                                        'var(--comp-field-fontsize-input-default)',
                                    fontWeight:
                                        'var(--comp-field-fontweight-input-default',
                                    color: 'var(--comp-field-fontcolour-default)',
                                }),
                                multiValue: (base) => ({
                                    ...base,
                                    whiteSpace: 'nowrap',
                                }),
                                indicatorsContainer: (base) => ({
                                    ...base,
                                    flexShrink: 0,
                                }),
                                placeholder: (baseStyles) => ({
                                    ...baseStyles,
                                    fontSize:
                                        'var(--comp-field-fontsize-input-default)',
                                    fontWeight:
                                        'var(--comp-field-fontweight-input-default',
                                    color: 'var(--comp-field-fontcolour-default)',
                                }),
                                option: (baseStyles, state) => ({
                                    ...baseStyles,
                                    color: state.isSelected
                                        ? state.isFocused
                                            ? '#fff'
                                            : 'var(--comp-field-fontcolour-input-default)'
                                        : state.isFocused
                                          ? '#fff'
                                          : 'var(--comp-field-fontcolour-input-default)', // Font color for options
                                    backgroundColor: state.isFocused
                                        ? 'var(--comp-field-bgcolor-menuoptionactive)'
                                        : 'var(--comp-field-fontcolor-menu-active)', // Background for focused options
                                    fontSize: '14px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    paddingLeft: '8px',
                                    paddingRight: '8px',
                                    paddingTop: '4px',
                                    paddingBottom: '4px',
                                }),
                            }}
                        >
                            {/* <option value="" disabled>
                
            </option>
            {optionList.map((option: DropDownOption, i: number) => (
                <option key={i} value={option.value}>
                    {option.name}
                </option>
            ))} */}
                        </Select>
                    </div>
                    {form ? (
                        <Errors
                            fieldName={name}
                            validation={form[name] ? form[name] : undefined}
                        />
                    ) : (
                        ''
                    )}
                </div>
            ) : null}
        </React.Fragment>
    )
}
export default MultiSelectNoInput
